import { useGetIntegrations } from "../../lib/hooks"
import { Button } from "@/components/ui/button"
import { queryClient } from "@/lib/react-query"
import { useToast } from "@/components/ui/use-toast"
import { useCallback, useEffect, useRef, useState } from "react"
import { cn } from "@/lib/utils"
import { client, validateClientProperty } from "@/lib/ts-rest"
import { ReloadIcon } from "@radix-ui/react-icons"
import { Input } from "../ui/input"
import { MapPin, Search, Building2 } from "lucide-react"
import { CheckBadgeIcon } from "@heroicons/react/24/outline"
import { trackEvent } from "@/lib/log"

type SearchResultLocation = {
  type: "location"
  locationName: string
  locationAddress: string
  latitude: number
  longitude: number
}

type SearchResultMosque = {
  type: "mosque"
  mosqueId: number
  name: string
  address: string
}

type SearchResult = SearchResultLocation | SearchResultMosque

export function IntegrationLocation({
  integrationId,
}: {
  integrationId: number
}) {
  const { data } = useGetIntegrations()
  const integration = data?.body.integrations.find(
    (item) => item.integration.id === Number(integrationId),
  )
  const hasExistingLocation = Boolean(integration?.locationName)

  const { mutate: updateIntegration, isPending: isPendingUpdate } =
    client.updateIntegration.useMutation({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [validateClientProperty("getIntegrations")],
        })
        toast({ title: "Location updated." })
        setEdit(false)
        setSelected(null)
        setResults(null)
        setSearchText("")
      },
    })

  function trackLocationSaved(result: SearchResult) {
    if (result.type === "mosque") {
      trackEvent("saved a location", { integrationId, type: "mosque", name: result.name })
    } else {
      trackEvent("saved a location", { integrationId, type: "coordinates", name: result.locationName })
    }
  }

  const { mutateAsync: searchLocationsAsync, isPending: isSearching } =
    client.searchLocations.useMutation()

  const { toast } = useToast()

  const [edit, setEdit] = useState(!hasExistingLocation)
  const [searchText, setSearchText] = useState("")
  const [selected, setSelected] = useState<SearchResult | null>(null)
  const [gettingLocation, setGettingLocation] = useState(false)
  const [results, setResults] = useState<SearchResult[] | null>(null)

  const autoTriggeredGeoRef = useRef(false)

  useEffect(() => {
    if (!hasExistingLocation) {
      setEdit(true)
    }
  }, [hasExistingLocation])

  const handleSearchResponse = useCallback(
    (body: { results: SearchResult[] }) => {
      setResults(body.results)
      if (body.results[0]) {
        setSelected(body.results[0])
      } else {
        setSelected(null)
      }
    },
    [],
  )

  const getDeviceLocation = useCallback(() => {
    if (!("geolocation" in navigator)) {
      toast({
        title: "Your browser doesn't support location features.",
        variant: "destructive",
      })
      return
    }

    trackEvent("requested device location")
    setGettingLocation(true)
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const response = await searchLocationsAsync({
            body: {
              type: "coordinates",
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
          })
          handleSearchResponse(response.body)
        } catch {
          toast({
            title: "Error searching for location.",
            variant: "destructive",
          })
        } finally {
          setGettingLocation(false)
        }
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          toast({
            title:
              "Please grant us location permissions through your browser settings.",
            variant: "destructive",
          })
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          toast({
            title: "Your device says that your location is unavailable.",
            variant: "destructive",
          })
        } else if (error.code === error.TIMEOUT) {
          toast({
            title: "Your device has timed out when getting your location.",
            variant: "destructive",
          })
        }
        setGettingLocation(false)
      },
    )
  }, [searchLocationsAsync, handleSearchResponse, toast])

  useEffect(() => {
    if (!hasExistingLocation && !autoTriggeredGeoRef.current) {
      autoTriggeredGeoRef.current = true
      getDeviceLocation()
    }
  }, [hasExistingLocation, getDeviceLocation])

  async function submitSearch() {
    if (!searchText.trim()) return
    trackEvent("searched for a location", { type: "address", query: searchText.trim() })
    try {
      const response = await searchLocationsAsync({
        body: { type: "address", query: searchText.trim() },
      })
      handleSearchResponse(response.body)
    } catch {
      toast({
        title: "Error searching for location.",
        variant: "destructive",
      })
    }
  }

  function saveSelection() {
    if (!selected) return
    trackLocationSaved(selected)

    if (selected.type === "mosque") {
      updateIntegration({
        body: {
          integration: {
            prayerTimeSource: "Mosque",
            mosqueId: selected.mosqueId,
          },
        },
        params: { integrationId: String(integrationId) },
      })
    } else {
      const { latitude, longitude, locationName, locationAddress } = selected
      updateIntegration({
        body: {
          integration: { prayerTimeSource: "PrayerTimeConfiguration" },
          prayerTimeConfiguration: {
            latitude,
            longitude,
            locationName,
            locationAddress,
          },
        },
        params: { integrationId: String(integrationId) },
      })
    }
  }

  const isLoading = isSearching || gettingLocation

  function isSameResult(a: SearchResult, b: SearchResult) {
    if (a.type === "mosque" && b.type === "mosque")
      return a.mosqueId === b.mosqueId
    if (a.type === "location" && b.type === "location")
      return a.latitude === b.latitude && a.longitude === b.longitude
    return false
  }

  return (
    <div>
      {hasExistingLocation && !edit && (
        <>
          <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4 w-fit">
            <CheckBadgeIcon className="h-6 w-6 text-green-700 shrink-0" />
            <div>
              <div className="font-medium text-green-700">
                {integration?.locationName}
              </div>
              <div className="text-xs text-green-600">
                {integration?.locationAddress}
              </div>
            </div>
          </div>
          <Button variant="outline" className="mt-3" onClick={() => setEdit(true)}>
            Change
          </Button>
        </>
      )}
      {edit && (
        <div>
          <div className="flex gap-2 max-w-md">
            <div className="relative flex-grow">
              <Input
                placeholder="Search for an address or mosque..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    submitSearch()
                  }
                }}
                className="pr-10"
              />
              <button
                type="button"
                onClick={submitSearch}
                disabled={isLoading || !searchText.trim()}
                className="absolute right-0 top-0 h-full px-3 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
              >
                {isSearching ? (
                  <ReloadIcon className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={getDeviceLocation}
              disabled={isLoading}
              title="Use current location"
              className="shrink-0"
            >
              {gettingLocation ? (
                <ReloadIcon className="h-4 w-4 animate-spin" />
              ) : (
                <MapPin className="h-4 w-4" />
              )}
            </Button>
          </div>

          {results && results.length > 0 && (
            <div className="mt-3 flex flex-col gap-2 max-w-md">
              {results.map((item, i) => {
                const isSelected = selected
                  ? isSameResult(selected, item)
                  : false
                const isMosque = item.type === "mosque"
                const name = isMosque ? item.name : item.locationName
                const address = isMosque ? item.address : item.locationAddress

                return (
                  <button
                    key={isMosque ? `mosque-${item.mosqueId}` : `loc-${i}`}
                    type="button"
                    onClick={() => { trackEvent("selected a location result", { type: item.type, name: isMosque ? item.name : item.locationName }); setSelected(item) }}
                    className={cn(
                      "text-left p-3 rounded-lg border-2 transition-colors",
                      isSelected
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-gray-300 bg-white",
                    )}
                  >
                    <div className="flex items-start gap-2">
                      {isMosque ? (
                        <Building2 className="h-4 w-4 mt-0.5 text-primary shrink-0" />
                      ) : (
                        <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                      )}
                      <div className="min-w-0">
                        {isMosque && (
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-primary mb-0.5">
                            Verified
                          </div>
                        )}
                        <div className="font-medium text-sm truncate">
                          {name}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {address}
                        </div>
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          )}

          <div className="flex gap-2 mt-4">
            {hasExistingLocation && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setEdit(false)
                  setSelected(null)
                  setResults(null)
                  setSearchText("")
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              onClick={saveSelection}
              disabled={!selected || isPendingUpdate}
            >
              {isPendingUpdate ? (
                <>
                  <ReloadIcon className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
